import type { RealtimeMessage } from '@atlas/types'
import { buildUserTopic, REALTIME_SUBSCRIBE_LEAF } from '@atlas/types'
import mqtt, { type MqttClient } from 'mqtt'
import { createClientId } from '@/lib/client-id'
import { logger } from '@/lib/logger'
import type { RealtimePublicConfig } from './realtime-config'
import { dispatchRealtimeMessage, RealtimeConnectionState, setRealtimeConnectionState } from './realtime-dispatcher'

// The MQTT transport (ADR-0020), deliberately a plain module rather than a hook:
// the connection outlives every render, and keeping it out of React makes the
// lifecycle — token, backoff, resubscribe, teardown — testable without mounting
// anything. `RealtimeProvider` is a thin wrapper that starts and stops one of these.

const log = logger.child('realtime')

/**
 * SUBACK reason codes at or above 0x80 are failures — `128` is "not authorized".
 * They arrive in the granted-subscription list rather than as an error.
 */
const SUBACK_FAILURE = 128

/** First retry delay; doubles per consecutive failure. */
const BASE_RETRY_MS = 1000
/** Ceiling for the doubling, before jitter. */
const MAX_RETRY_MS = 30_000
/**
 * Fraction of the computed delay that is randomised away. Every open tab of every
 * user retries after the same network event, so an exact delay would march them
 * into the authorizer in lockstep; spreading each wait over 50–100% of its
 * nominal value breaks that convoy without meaningfully slowing recovery.
 */
const JITTER_RATIO = 0.5

export interface RealtimeConnectionDeps {
	config: RealtimePublicConfig
	userId: string
	/** Resolves the current access token, or null when the session cannot supply one. */
	getToken: () => Promise<string | null>
	/**
	 * Invoked after a subscription is re-established following a drop, never on the
	 * first connect. Messages sent while disconnected are gone, so the owner uses
	 * this to re-read rather than to replay.
	 */
	onResubscribed?: () => void
}

export interface RealtimeConnection {
	/** Tears the connection down and cancels any pending retry. Idempotent. */
	stop: () => void
}

/**
 * Opens and maintains one MQTT-over-WSS connection for the signed-in user, and
 * feeds every valid message into the dispatcher.
 *
 * Returns as soon as the first attempt is scheduled — connecting is asynchronous
 * and the caller has nothing to await. Failures are handled internally by
 * retrying with jittered backoff; the caller only ever stops the connection.
 *
 * @param {RealtimeConnectionDeps} deps - Connection coordinates, the user's id, and the token source.
 * @returns {RealtimeConnection} Handle whose `stop()` ends the connection for good.
 */
export function startRealtimeConnection(deps: RealtimeConnectionDeps): RealtimeConnection {
	const topic = userTopic(deps.config.topicPrefix, deps.userId)
	const retries = new RetrySchedule()
	let client: MqttClient | null = null
	let stopped = false
	let hasConnectedBefore = false

	// Tears down the current client before queueing the next attempt. Both failure
	// paths need it: a drop leaves a dead client, and a refused subscription leaves
	// a live one keepalive-pinging a socket that will never deliver.
	function scheduleRetry(): void {
		teardownClient()
		if (stopped) return
		setRealtimeConnectionState(RealtimeConnectionState.RECONNECTING)
		retries.schedule(() => void attemptConnect())
	}

	async function attemptConnect(): Promise<void> {
		if (stopped) return

		const token = await deps.getToken()
		if (stopped) return
		// A null token is a failed renewal, not a permanent state — the user may
		// still have a recoverable session. Back off rather than retrying in a tight
		// loop, which would hammer the token endpoint for as long as it stays broken.
		if (!token) {
			log.warn('No access token for the realtime connection; backing off')
			scheduleRetry()
			return
		}

		client = openClient(deps.config, token, topic, {
			onConnect: () => {
				retries.reset()
				setRealtimeConnectionState(RealtimeConnectionState.CONNECTED)
				if (hasConnectedBefore) deps.onResubscribed?.()
				hasConnectedBefore = true
			},
			// Both failures retry: a drop needs a fresh token (mqtt's own retry would
			// reuse the stale one), and a refusal needs it because nothing closed.
			onClosed: scheduleRetry,
			onSubscribeFailed: scheduleRetry,
		})
	}

	function teardownClient(): void {
		if (!client) return
		const closing = client
		client = null
		closing.removeAllListeners()
		closing.end(true)
	}

	void attemptConnect()

	return {
		stop: () => {
			stopped = true
			retries.cancel()
			teardownClient()
			setRealtimeConnectionState(RealtimeConnectionState.OFFLINE)
		},
	}
}

/** Owns the retry timer and the consecutive-failure count behind the backoff. */
class RetrySchedule {
	private timer: ReturnType<typeof setTimeout> | null = null
	private attempt = 0

	/**
	 * Queues the next attempt after a jittered backoff delay.
	 *
	 * @param {() => void} run - The retry to perform.
	 */
	schedule(run: () => void): void {
		const delay = backoffDelayMs(this.attempt)
		this.attempt += 1
		this.timer = setTimeout(() => {
			this.timer = null
			run()
		}, delay)
	}

	/** Returns the delay to its base, after a connection succeeds. */
	reset(): void {
		this.attempt = 0
	}

	/** Drops any queued attempt. */
	cancel(): void {
		if (this.timer === null) return
		clearTimeout(this.timer)
		this.timer = null
	}
}

interface ClientCallbacks {
	onConnect: () => void
	onClosed: () => void
	/**
	 * The socket opened but the subscription did not take.
	 *
	 * Distinct from `onClosed` because nothing closes: a SUBACK refusal is an
	 * application-level answer, so the connection stays open and keepalive holds it
	 * there. Without this the session would sit connected, subscribed to nothing,
	 * for the lifetime of the tab.
	 */
	onSubscribeFailed: () => void
}

/**
 * Creates the mqtt client and wires its lifecycle.
 *
 * `manualConnect` plus an explicit `connect()` at the end is what makes the
 * ordering safe: the `message` listener is registered before the socket opens, so
 * a message arriving in the same tick as SUBACK cannot be dropped.
 *
 * @param {RealtimePublicConfig} config - Endpoint and authorizer coordinates.
 * @param {string} token - Cognito access token, passed as the MQTT password.
 * @param {string} topic - Fully-qualified topic filter to subscribe to.
 * @param {ClientCallbacks} callbacks - Connect and close notifications for the owner.
 * @returns {MqttClient} The connecting client.
 */
function openClient(
	config: RealtimePublicConfig,
	token: string,
	topic: string,
	callbacks: ClientCallbacks,
): MqttClient {
	const url = `wss://${config.endpoint}/mqtt?x-amz-customauthorizer-name=${encodeURIComponent(config.authorizer)}`
	const client = mqtt.connect(url, {
		protocolVersion: 5,
		manualConnect: true,
		// The IoT custom authorizer reads the credential from the password field;
		// a non-empty username makes it reject the connection.
		username: '',
		password: token,
		clientId: createClientId('client'),
		// Reconnection is owned here, not by mqtt: each attempt needs a freshly
		// resolved token, which mqtt's internal retry would not do.
		reconnectPeriod: 0,
	})

	log.debug('Connecting', { endpoint: config.endpoint, authorizer: config.authorizer, topic })

	client.on('message', (messageTopic, payload) => {
		log.debug('Message received', { topic: messageTopic, bytes: payload.length })
		handleIncoming(payload)
	})

	client.on('connect', () => {
		log.debug('Connected; subscribing', { topic })
		// Resubscribing on every connect (not only the first) is what makes a
		// reconnect deliver again — a new session starts with no subscriptions.
		client.subscribe(topic, { qos: 1 }, (error, granted) => {
			if (error) {
				log.error('Realtime subscribe failed', { topic, error })
				callbacks.onSubscribeFailed()
				return
			}

			// A refusal arrives as a SUBACK reason code, NOT as an error: mqtt reports
			// success and puts 128 ("not authorized") in `granted`. Treating that as a
			// subscription is how a session ends up connected, believing it is
			// subscribed, and silently receiving nothing — so it is checked explicitly.
			const refused = !granted?.length || granted.some(({ qos }) => qos >= SUBACK_FAILURE)
			if (refused) {
				log.error('Realtime subscribe refused by the broker', { topic, granted })
				callbacks.onSubscribeFailed()
				return
			}

			log.debug('Subscribed', { topic, granted })
			callbacks.onConnect()
		})
	})

	client.on('error', (error) => {
		log.warn('Realtime connection error', error)
	})

	client.on('close', () => {
		callbacks.onClosed()
	})

	client.connect()
	return client
}

/**
 * Parses and validates one wire payload, then dispatches it.
 *
 * The contract is shared with the publisher, so drift is a bug rather than
 * untrusted input to tolerate quietly: it is logged at error level in dev, where
 * someone can act on it, and at warn in production, where a noisy console helps
 * nobody. Either way the message is dropped and no handler sees a malformed shape.
 *
 * @param {Uint8Array} payload - Raw MQTT payload.
 */
function handleIncoming(payload: Uint8Array): void {
	let parsed: unknown
	try {
		parsed = JSON.parse(new TextDecoder().decode(payload))
	} catch (error) {
		reportInvalidMessage('Realtime payload was not valid JSON', error)
		return
	}

	if (!isRealtimeMessage(parsed)) {
		reportInvalidMessage('Realtime payload did not match the message contract', parsed)
		return
	}

	dispatchRealtimeMessage(parsed)
}

/**
 * Reports a contract violation loudly in dev and quietly in production.
 *
 * @param {string} message - What was wrong with the payload.
 * @param {unknown} detail - The parse error or the rejected value.
 */
function reportInvalidMessage(message: string, detail: unknown): void {
	if (import.meta.env.DEV) {
		log.error(message, detail)
		return
	}
	log.warn(message, detail)
}

/**
 * Structural check against the shared message contract. Only the fields the
 * dispatcher's filters read are required; `data` is deliberately unconstrained
 * because messages carry references, not typed content.
 *
 * @param {unknown} value - Parsed payload.
 * @returns {boolean} Whether the value is a usable {@link RealtimeMessage}.
 */
function isRealtimeMessage(value: unknown): value is RealtimeMessage {
	if (typeof value !== 'object' || value === null) return false
	const candidate = value as Partial<RealtimeMessage>
	if (typeof candidate.type !== 'string' || typeof candidate.timestamp !== 'string') return false

	const entityRef: unknown = candidate.entityRef
	if (typeof entityRef !== 'object' || entityRef === null) return false
	const ref = entityRef as { type?: unknown; id?: unknown }
	return typeof ref.type === 'string' && typeof ref.id === 'string'
}

/**
 * Builds the topic filter to subscribe to, keeping `buildUserTopic` the one
 * authority on topic shape.
 *
 * The config supplies `{app}/{stage}` pre-assembled while the helper takes the two
 * parts separately, so the prefix is split back apart and handed over rather than
 * concatenated here. Interpolating around the prefix would be a second place that
 * knows the `.../user/{userId}/{entityType}` layout, and the authorizer computes
 * the grant with the helper — so any drift between the two would present as a
 * subscription that is accepted and then never delivers.
 *
 * @param {string} topicPrefix - The `{app}/{stage}` namespace from configuration.
 * @param {string} userId - The resolved ATLAS user id the authorizer granted.
 * @returns {string} The topic filter covering every entity type for this user.
 */
function userTopic(topicPrefix: string, userId: string): string {
	const separator = topicPrefix.indexOf('/')
	if (separator <= 0 || separator === topicPrefix.length - 1) {
		throw new Error(`Realtime topic prefix must be "{app}/{stage}", received "${topicPrefix}"`)
	}
	return buildUserTopic({
		app: topicPrefix.slice(0, separator),
		stage: topicPrefix.slice(separator + 1),
		userId,
		// The SUBSCRIBE leaf, not the policy leaf: MQTT has no `*` wildcard, so a
		// filter ending in `*` is accepted and then matches nothing.
		entityType: REALTIME_SUBSCRIBE_LEAF,
	})
}

/**
 * Exponential backoff with jitter, capped so a long outage still retries at a
 * steady rate rather than drifting into hours.
 *
 * @param {number} attempt - Count of consecutive failures so far (0 for the first retry).
 * @returns {number} Delay in milliseconds before the next attempt.
 */
export function backoffDelayMs(attempt: number): number {
	const capped = Math.min(BASE_RETRY_MS * 2 ** attempt, MAX_RETRY_MS)
	return Math.round(capped * (1 - JITTER_RATIO + Math.random() * JITTER_RATIO))
}
